/**
 * Global image cache that persists across annotation switches
 * This prevents re-downloading the same images when switching between annotations on the same task
 */

import { getAuthToken } from "./auth-token";

/**
 * Custom error class for image cache errors that should not be sent to Sentry
 * These are expected errors (network issues, invalid images) not code bugs
 */
class ImageCacheError extends Error {
  sentry_skip = true;

  constructor(message: string) {
    super(message);
    this.name = "ImageCacheError";
  }
}

type CachedImage = {
  blobUrl: string;
  naturalWidth: number;
  naturalHeight: number;
  timestamp: number;
  /** Reference count - number of active users of this blob URL */
  refCount: number;
  /** Original URL for re-fetching if blob URL becomes invalid */
  originalUrl: string;
};

class ImageCacheManager {
  private cache: Map<string, CachedImage> = new Map();
  private pendingLoads: Map<string, Promise<CachedImage>> = new Map();
  /** Track revoked blob URLs to detect invalid references */
  private revokedUrls: Set<string> = new Set();

  // Cache for 30 minutes by default
  private readonly maxAge = 30 * 60 * 1000;
  // Maximum cache size (100 images)
  private readonly maxSize = 100;
  // Minimum blob size in bytes (reject empty blobs)
  private readonly minBlobSize = 100;

  /** Normalize a URL for cache lookups by stripping volatile query params.
   * Two requests for the same image asset shouldn't miss cache just because
   * the JWT was refreshed between them or the URL has a fresh presign signature.
   * The actual XHR still uses the original URL (with token/signature) — only
   * the cache key is normalized. */
  private normalizeCacheKey(url: string): string {
    if (!url) return url;
    // Cheap path: nothing to strip.
    if (
      !/[?&](token|X-Amz-Signature|X-Amz-Date|X-Amz-Expires|X-Amz-Credential|X-Amz-SignedHeaders|X-Amz-Security-Token)=/i.test(
        url,
      )
    ) {
      return url;
    }
    const qIdx = url.indexOf("?");
    if (qIdx === -1) return url;
    const base = url.slice(0, qIdx);
    const params = new URLSearchParams(url.slice(qIdx + 1));
    for (const key of [
      "token",
      "X-Amz-Signature",
      "X-Amz-Date",
      "X-Amz-Expires",
      "X-Amz-Credential",
      "X-Amz-SignedHeaders",
      "X-Amz-Security-Token",
    ]) {
      params.delete(key);
    }
    const rest = params.toString();
    return rest ? `${base}?${rest}` : base;
  }

  /**
   * Get a cached image's blob URL
   * Returns undefined if the cache entry is invalid or blob URL was revoked
   */
  get(url: string): CachedImage | undefined {
    const key = this.normalizeCacheKey(url);
    const cached = this.cache.get(key);
    if (!cached) return undefined;

    // Check if cache entry is still valid (age check)
    if (Date.now() - cached.timestamp > this.maxAge) {
      this.safeRevokeBlobUrl(cached);
      this.cache.delete(key);
      return undefined;
    }

    // Check if blob URL has been revoked (validity check)
    if (this.revokedUrls.has(cached.blobUrl)) {
      this.cache.delete(key);
      return undefined;
    }

    return cached;
  }

  /**
   * Increment reference count for a cached image
   * Call this when starting to use a cached blob URL
   */
  addRef(url: string): void {
    const cached = this.cache.get(this.normalizeCacheKey(url));
    if (cached) {
      cached.refCount++;
    }
  }

  /**
   * Decrement reference count for a cached image
   * Call this when done using a cached blob URL
   */
  releaseRef(url: string): void {
    const cached = this.cache.get(this.normalizeCacheKey(url));
    if (cached && cached.refCount > 0) {
      cached.refCount--;
    }
  }

  /**
   * Safely revoke a blob URL and track it as revoked
   */
  private safeRevokeBlobUrl(cached: CachedImage): void {
    if (cached.blobUrl && !this.revokedUrls.has(cached.blobUrl)) {
      URL.revokeObjectURL(cached.blobUrl);
      this.revokedUrls.add(cached.blobUrl);
      // Limit the size of revoked URLs set to prevent memory leaks
      if (this.revokedUrls.size > 1000) {
        const iterator = this.revokedUrls.values();
        for (let i = 0; i < 500; i++) {
          const next = iterator.next();
          if (next.done) break;
          this.revokedUrls.delete(next.value);
        }
      }
    }
  }

  /**
   * Check if an image is currently being loaded
   */
  isLoading(url: string): boolean {
    return this.pendingLoads.has(this.normalizeCacheKey(url));
  }

  /**
   * Get the pending load promise for an image
   */
  getPendingLoad(url: string): Promise<CachedImage> | undefined {
    return this.pendingLoads.get(this.normalizeCacheKey(url));
  }

  /**
   * Load an image and cache it
   * If the same image is already being loaded, return the existing promise (deduplication)
   */
  async load(url: string, crossOrigin?: string, onProgress?: (progress: number) => void): Promise<CachedImage> {
    const key = this.normalizeCacheKey(url);
    // Check cache first
    const cached = this.get(url);
    if (cached) {
      onProgress?.(1);
      return cached;
    }

    // Check if already loading (deduplication)
    const pending = this.pendingLoads.get(key);
    if (pending) {
      return pending;
    }

    // Start new load
    const loadPromise = this.loadImage(url, crossOrigin, onProgress);
    this.pendingLoads.set(key, loadPromise);

    try {
      const result = await loadPromise;
      return result;
    } finally {
      this.pendingLoads.delete(key);
    }
  }

  private resolveUrl(url: string): string {
    const hostname = (window as any).APP_SETTINGS?.hostname;
    if (hostname && url.startsWith("/data/")) {
      const token = getAuthToken();
      const absoluteUrl = new URL(url, hostname).toString();
      return token ? `${absoluteUrl}${absoluteUrl.includes("?") ? "&" : "?"}token=${token}` : absoluteUrl;
    }
    return url;
  }

  private async loadImage(
    url: string,
    crossOrigin?: string,
    onProgress?: (progress: number) => void,
  ): Promise<CachedImage> {
    try {
      return await this.fetchImage(url, crossOrigin, onProgress);
    } catch (e) {
      // On 401, try refreshing the token and retry once
      if (e instanceof ImageCacheError && e.message.includes("status: 401")) {
        const refreshed = await this.tryRefreshToken();
        if (refreshed) {
          return await this.fetchImage(url, crossOrigin, onProgress);
        }
      }
      throw e;
    }
  }

  private _refreshPromise: Promise<boolean> | null = null;

  private async tryRefreshToken(): Promise<boolean> {
    if (this._refreshPromise) return this._refreshPromise;

    this._refreshPromise = (async () => {
      const refreshToken = typeof localStorage !== "undefined" ? localStorage.getItem("jwt_refresh_token") : null;
      if (!refreshToken) return false;

      const hostname = (window as any).APP_SETTINGS?.hostname || "";
      try {
        const response = await fetch(`${hostname}/api/token/refresh/`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ refresh: refreshToken }),
        });
        if (!response.ok) return false;
        const data = await response.json();
        localStorage.setItem("jwt_access_token", data.access);
        return true;
      } catch {
        return false;
      } finally {
        this._refreshPromise = null;
      }
    })();

    return this._refreshPromise;
  }

  private fetchImage(url: string, crossOrigin?: string, onProgress?: (progress: number) => void): Promise<CachedImage> {
    return new Promise((resolve, reject) => {
      const resolvedUrl = this.resolveUrl(url);
      const xhr = new XMLHttpRequest();
      xhr.responseType = "blob";

      xhr.addEventListener("load", async () => {
        if (xhr.readyState === 4 && xhr.status === 200) {
          const blob = xhr.response as Blob;

          // Validate blob size - reject empty or too small blobs
          if (!blob || blob.size < this.minBlobSize) {
            reject(
              new ImageCacheError(`Empty or invalid image data received: ${url} (size: ${blob?.size ?? 0} bytes)`),
            );
            return;
          }

          const isGenericType = !blob.type || blob.type.includes("octet-stream");
          if (!isGenericType && !blob.type.startsWith("image/")) {
            reject(new ImageCacheError(`Invalid content type for image: ${blob.type} (url: ${url})`));
            return;
          }

          const blobUrl = URL.createObjectURL(blob);

          const img = new Image();
          if (crossOrigin) img.crossOrigin = crossOrigin;

          img.onload = () => {
            if (img.naturalWidth === 0 || img.naturalHeight === 0) {
              URL.revokeObjectURL(blobUrl);
              this.revokedUrls.add(blobUrl);
              reject(new ImageCacheError(`Image has invalid dimensions (0x0): ${url}`));
              return;
            }

            const cachedImage: CachedImage = {
              blobUrl,
              naturalWidth: img.naturalWidth,
              naturalHeight: img.naturalHeight,
              timestamp: Date.now(),
              refCount: 0,
              originalUrl: url,
            };

            this.ensureCacheSize();
            this.cache.set(this.normalizeCacheKey(url), cachedImage);

            resolve(cachedImage);
          };

          img.onerror = () => {
            URL.revokeObjectURL(blobUrl);
            this.revokedUrls.add(blobUrl);
            reject(new ImageCacheError(`Failed to load image dimensions: ${url}`));
          };

          img.src = blobUrl;
        } else {
          reject(new ImageCacheError(`Failed to download image (status: ${xhr.status}): ${url}`));
        }
      });

      xhr.addEventListener("progress", (e) => {
        if (e.lengthComputable) {
          onProgress?.(e.loaded / e.total);
        }
      });

      xhr.addEventListener("error", () => {
        reject(new ImageCacheError(`Network error loading image: ${url}`));
      });

      xhr.open("GET", resolvedUrl);

      // Only send auth header for same-origin or backend URLs, not external (e.g. S3)
      const isSameOrigin = resolvedUrl.startsWith("/") || resolvedUrl.startsWith(window.location.origin);
      const backendHost = (window as any).APP_SETTINGS?.hostname;
      const isBackend = backendHost && resolvedUrl.startsWith(backendHost);
      if (isSameOrigin || isBackend) {
        const token = getAuthToken();
        if (token) {
          xhr.setRequestHeader("Authorization", `Bearer ${token}`);
        }
      }

      xhr.send();
    });
  }

  private ensureCacheSize(): void {
    if (this.cache.size >= this.maxSize) {
      // Remove oldest entries that are not in use (refCount === 0)
      const entriesToRemove = this.cache.size - this.maxSize + 1;
      let removed = 0;

      // First pass: remove entries with refCount === 0
      for (const [key, value] of this.cache) {
        if (removed >= entriesToRemove) break;
        // Only remove entries that are not actively in use
        if (value.refCount === 0) {
          this.safeRevokeBlobUrl(value);
          this.cache.delete(key);
          removed++;
        }
      }

      // If we couldn't remove enough entries (all in use), log a warning
      // but don't force-remove in-use entries to prevent rendering failures
      if (removed < entriesToRemove) {
        console.warn(
          `ImageCache: Unable to evict ${entriesToRemove - removed} entries (all in use). ` +
            `Cache size: ${this.cache.size}, active refs: ${this.getActiveRefCount()}`,
        );
      }
    }
  }

  /**
   * Get count of images with active references
   */
  private getActiveRefCount(): number {
    let count = 0;
    for (const value of this.cache.values()) {
      if (value.refCount > 0) count++;
    }
    return count;
  }

  /**
   * Force remove a specific image from cache
   * Used for error recovery when a cached blob URL becomes invalid
   */
  forceRemove(url: string): void {
    const key = this.normalizeCacheKey(url);
    const cached = this.cache.get(key);
    if (cached) {
      this.safeRevokeBlobUrl(cached);
      this.cache.delete(key);
    }
  }

  /**
   * Force clear all cached images and release memory
   * Called when LSF is destroyed to prevent memory leaks
   */
  forceClear(): void {
    for (const cached of this.cache.values()) {
      this.safeRevokeBlobUrl(cached);
    }
    this.cache.clear();
    this.pendingLoads.clear();
  }
}

// Singleton instance - persists across annotation switches
export const imageCache = new ImageCacheManager();
